import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { IconCheck, IconMinus } from '@tabler/icons-react';
import { cn } from '../tokens/cn';
import { focusRing, transitionBase } from '../tokens/variants';

export interface CheckboxProps
  extends ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  size?: 'sm' | 'md' | 'lg';
}

const sizeMap = {
  sm: { box: 'h-4 w-4', icon: 12 },
  md: { box: 'h-5 w-5', icon: 14 },
  lg: { box: 'h-6 w-6', icon: 16 },
};

export const Checkbox = forwardRef<
  ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, size = 'md', ...props }, ref) => {
  const { box, icon } = sizeMap[size];
  return (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(
        'shrink-0 rounded-tk-xs border border-[var(--tk-surface-border-strong)]',
        'bg-[var(--tk-surface-bg-elevated)]',
        'data-[state=checked]:bg-[var(--tk-brand-bg)] data-[state=checked]:border-[var(--tk-brand-bg)]',
        'data-[state=indeterminate]:bg-[var(--tk-brand-bg)] data-[state=indeterminate]:border-[var(--tk-brand-bg)]',
        'data-[state=checked]:text-[var(--tk-brand-fg)] data-[state=indeterminate]:text-[var(--tk-brand-fg)]',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        focusRing,
        transitionBase,
        box,
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
        {props.checked === 'indeterminate' ? <IconMinus size={icon} /> : <IconCheck size={icon} />}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
});

Checkbox.displayName = 'Checkbox';
